import { useState, useEffect, useCallback } from 'react';

interface NetworkStatus {
  isOnline: boolean;
  isSlowConnection: boolean;
  connectionType: string;
  downlink?: number; // Network speed in Mbps
  rtt?: number; // Round-trip time in ms
}

interface UseNetworkStatusReturn extends NetworkStatus {
  retryFailedRequests: () => void;
  getNetworkInfo: () => NetworkStatus;
}

// Queue for failed requests when offline
interface FailedRequest {
  id: string;
  url: string;
  config: Record<string, unknown>;
  timestamp: number;
  retryCount: number;
}

class OfflineRequestQueue {
  private queue: FailedRequest[] = [];
  private maxRetries = 3;
  private retryDelay = 2000; // 2 seconds

  add(request: FailedRequest) {
    this.queue.push(request);
    this.persistQueue();
  }

  async retryAll() {
    const requests = [...this.queue];
    this.queue = [];
    this.persistQueue();

    for (const request of requests) {
      if (request.retryCount < this.maxRetries) {
        try {
          // We'll integrate this with axios later
          console.info(`Retrying offline request: ${request.url}`);
          // await axios(request.config);
        } catch (_error) {
          request.retryCount++;
          if (request.retryCount < this.maxRetries) {
            setTimeout(() => this.add(request), this.retryDelay);
          } else {
            console.error(`Failed to retry request after ${this.maxRetries} attempts:`, request.url);
          }
        }
      }
    }
  }

  getQueueSize() {
    return this.queue.length;
  }

  private persistQueue() {
    try {
      sessionStorage.setItem('offlineRequestQueue', JSON.stringify(this.queue));
    } catch (error) {
      console.warn('Failed to persist offline request queue:', error);
    }
  }

  private restoreQueue() {
    try {
      const stored = sessionStorage.getItem('offlineRequestQueue');
      if (stored) {
        this.queue = JSON.parse(stored);
      }
    } catch (error) {
      console.warn('Failed to restore offline request queue:', error);
      this.queue = [];
    }
  }

  constructor() {
    this.restoreQueue();
  }
}

const offlineQueue = new OfflineRequestQueue();

// Type for Navigator Connection API (not officially supported yet)
interface NavigatorConnection {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  addEventListener?: (type: string, listener: () => void) => void;
  removeEventListener?: (type: string, listener: () => void) => void;
}

interface ExtendedNavigator extends Navigator {
  connection?: NavigatorConnection;
  mozConnection?: NavigatorConnection;
  webkitConnection?: NavigatorConnection;
}

export const useNetworkStatus = (): UseNetworkStatusReturn => {
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>(() => {
    const nav = navigator as ExtendedNavigator;
    const connection = nav.connection || nav.mozConnection || nav.webkitConnection;
    
    return {
      isOnline: navigator.onLine,
      isSlowConnection: connection ? connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g' : false,
      connectionType: connection ? connection.effectiveType || 'unknown' : 'unknown',
      downlink: connection?.downlink,
      rtt: connection?.rtt,
    };
  });

  const updateNetworkStatus = useCallback(() => {
    const nav = navigator as ExtendedNavigator;
    const connection = nav.connection || nav.mozConnection || nav.webkitConnection;
    
    const newStatus: NetworkStatus = {
      isOnline: navigator.onLine,
      isSlowConnection: connection ? connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g' : false,
      connectionType: connection ? connection.effectiveType || 'unknown' : 'unknown',
      downlink: connection?.downlink,
      rtt: connection?.rtt,
    };

    setNetworkStatus(prevStatus => {
      // Only update and log if status actually changed
      if (prevStatus.isOnline !== newStatus.isOnline) {
        if (newStatus.isOnline) {
          console.info('🌐 Connection restored! Retrying failed requests...');
          // Retry queued requests when back online
          setTimeout(() => offlineQueue.retryAll(), 500);
        } else {
          console.warn('🔌 You are offline. Requests will be queued and retried when connection is restored.');
        }
      }

      // Log slow connection warning
      if (!prevStatus.isSlowConnection && newStatus.isSlowConnection) {
        console.warn('🐌 Slow network detected. Some features may be delayed.');
      }

      return newStatus;
    });
  }, []);

  useEffect(() => {
    // Add event listeners for network status changes
    const handleOnline = () => updateNetworkStatus();
    const handleOffline = () => updateNetworkStatus();
    const handleConnectionChange = () => updateNetworkStatus();

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen to connection changes if supported
    const nav = navigator as ExtendedNavigator;
    const connection = nav.connection || nav.mozConnection || nav.webkitConnection;
    if (connection && connection.addEventListener) {
      connection.addEventListener('change', handleConnectionChange);
    }

    // Check status immediately
    updateNetworkStatus();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      
      if (connection && connection.removeEventListener) {
        connection.removeEventListener('change', handleConnectionChange);
      }
    };
  }, [updateNetworkStatus]);

  const retryFailedRequests = useCallback(() => {
    if (networkStatus.isOnline) {
      offlineQueue.retryAll();
      console.info('🔄 Retrying failed requests...');
    } else {
      console.warn('⚠️ Cannot retry requests while offline');
    }
  }, [networkStatus.isOnline]);

  const getNetworkInfo = useCallback(() => networkStatus, [networkStatus]);

  return {
    ...networkStatus,
    retryFailedRequests,
    getNetworkInfo,
  };
};

// Export the queue for use in axios interceptor
export { offlineQueue };
